import Header from '@/components/Header'
import useDocumentTitle from '@/components/Helmet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import axiosInstance from '@/lib/axiosInstance';
import { zodResolver } from '@hookform/resolvers/zod';
import { Avatar, Box, Card, Grid, Paper, Typography } from '@mui/material';
import { Eye, EyeOff } from 'lucide-react';
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

const validationSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(1, "Phone number is required"),
})

const passwordSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters long")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
    confirmPassword: z.string().min(1, "Confirm password is required"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords do not match",
  });

const Account = () => {
  useDocumentTitle("Account");
  const { toast } = useToast()
  const { control, formState: { errors }, handleSubmit, setValue, getValues } = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      // password: '',
      // confirmPassword: '',
    },
  });

  const { control: passwordControl, formState: { errors: passwordErrors }, handleSubmit: handlePasswordSubmit, reset: resetPasswordForm } = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const [userData, setUserData] = useState<any>(null);
  const [loader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    fetchUserData()
  }, [])

  const fetchUserData = async () => {
    try {
      setLoader(true)
      await axiosInstance.get("/api/auth/me")
        .then((res) => {
          setValue("firstName", res.data.data.firstName)
          setValue("lastName", res.data.data.lastName)
          setValue("email", res.data.data.email)
          setValue("phone", res.data.data.phone)
          // setValue("password", res.data.data.password)
          // setValue("confirmPassword", res.data.data.password)

          setUserData(res.data.data)
        })
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error!",
        description: error?.response?.data?.message || "Something went wrong while fetching user data",
      })
    }
    finally {
      setLoader(false)
    }
  }

  const submitData = async (data: any) => {
    try {
      setLoader(true)
      const { firstName, lastName, email, phone } = data;
      await axiosInstance.put("/api/auth/me", { firstName, lastName, email, phone })
        .then((res) => {
          if (res.data?.success) {
            toast({
              variant: "success",
              title: "Success!",
              description: res.data.message,
            })
          }
          else {
            toast({
              variant: "destructive",
              title: "Error!",
              description: res.data.message,
            })
          }
        })
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error!",
        description: error?.response?.data?.message || "Something went wrong",
      })
    }
    finally {
      setLoader(false)
    }
  }

  const updatePassword = async (data: any) => {
    try {
      setLoader(true)
      const { password, confirmPassword } = data;
      await axiosInstance.put("/api/auth/password", { password, confirmPassword })
        .then((res) => {
          if (res.data?.success) {
            toast({
              variant: "success",
              title: "Success!",
              description: res.data.message,
            })
            resetPasswordForm()
          }
          else {
            toast({
              variant: "destructive",
              title: "Error!",
              description: res.data.message,
            })
          }
        })
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error!",
        description: error?.response?.data?.message || "Something went wrong",
      })
    }
    finally {
      setLoader(false)
    }
  }
  return (
    <div className='min-h-screen bg-gray-50'>
      <Header />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <Paper elevation={0} sx={{ p: 4 }}>
          <form onSubmit={handleSubmit(submitData)}>
            <Grid container spacing={2}>
              <Grid size={{ sm: 4, xs: 12 }}>
                <div className='flex justify-center'>
                  <Avatar alt="Remy Sharp" sx={{ width: 150, height: 150 }}>
                    <Typography variant='h1'>{userData?.firstName?.charAt(0).toUpperCase()}</Typography>
                  </Avatar>
                </div>
              </Grid>
              <Grid size={{ sm: 8, xs: 12 }}>
                <Grid container spacing={2}>
                  <Grid size={{ sm: 6, xs: 12 }}>
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field }) => (
                        <>
                          <Label htmlFor="firstName">First Name</Label>
                          <Input {...field} placeholder="First Name" id='firstName' />
                        </>
                      )}
                    />
                    {
                      errors.firstName && <span className='text-red-500'>{errors.firstName.message}</span>
                    }

                  </Grid>
                  <Grid size={{ sm: 6, xs: 12 }}>
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field }) => (
                        <>
                          <Label htmlFor="lastName">Last Name</Label>
                          <Input {...field} placeholder="Last Name" id='lastName' />
                        </>
                      )}
                    />
                    {
                      errors.lastName && <span className='text-red-500'>{errors.lastName.message}</span>
                    }
                  </Grid>
                  <Grid size={{ sm: 6, xs: 12 }}>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <>
                          <Label htmlFor="email">Email</Label>
                          <Input {...field} placeholder="Enter Email" id='email' disabled />
                        </>
                      )}
                    />
                    {
                      errors.email && <span className='text-red-500'>{errors.email.message}</span>
                    }
                  </Grid>
                  <Grid size={{ sm: 6, xs: 12 }}>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <>
                          <Label htmlFor="phone">Phone</Label>
                          <Input {...field} placeholder="Phone" id='phone' />
                        </>
                      )}
                    />
                    {
                      errors.phone && <span className='text-red-500'>{errors.phone.message}</span>
                    }
                  </Grid>
                  <Grid size={{ sm: 12, xs: 12 }}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: "space-between" }}>
                      <Button type='button' variant="error">Cancel</Button>
                      <Button type='submit'>Update</Button>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
          <br />
          <Typography variant='h5' className='mb-4'>Update Password</Typography>
          <form onSubmit={handlePasswordSubmit(updatePassword)}>
            <Grid container spacing={2}>
              <Grid size={{ sm: 5, xs: 12 }}>
                <Controller
                  name="password"
                  control={passwordControl}
                  render={({ field }) => (
                    <div className='relative'>
                      <Label htmlFor="password">Password</Label>
                      <Input {...field} placeholder="Password" type={showPassword ? "text" : "password"} id='password' />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-3 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        data-testid="button-toggle-confirm-password"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </Button>
                    </div>
                  )}
                />
                {
                  passwordErrors.password && <span className='text-red-500'>{passwordErrors.password.message}</span>
                }
              </Grid>
              {/* Confirm password */}
              <Grid size={{ sm: 5, xs: 12 }}>
                <Controller
                  name="confirmPassword"
                  control={passwordControl}
                  render={({ field }) => (
                    <div className='relative'>
                      <Label htmlFor="confirmPassword">Confirm Password</Label>
                      <Input {...field} placeholder="Confirm Password" type={showConfirmPassword ? "text" : "password"} id='confirmPassword' />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-3 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        data-testid="button-toggle-confirm-password"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </Button>
                    </div>
                  )}
                />
                {
                  passwordErrors.confirmPassword && <span className='text-red-500'>{passwordErrors.confirmPassword.message}</span>
                }
              </Grid>


              <Grid size={{ sm: 2, xs: 12 }}>
                <Button type='submit' className='mt-6 w-full'>Update Password</Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </section>

    </div>
  )
}

export default Account